import { useState } from 'react';
import { useAuth } from '../context/useAuth';
import { useLanguage } from '../i18n';
import { Check, Crown, ArrowRight, Loader2, AlertCircle, Zap } from 'lucide-react';

interface UpgradePanelProps {
  onBack?: () => void;
}

export default function UpgradePanel({ onBack }: UpgradePanelProps) {
  const { user, upgradePlan } = useAuth();
  const { t } = useLanguage();
  const u = t.upgrade;
  const [selected, setSelected] = useState<'founder' | 'scale'>('founder');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const plans: ('founder' | 'scale')[] = ['founder', 'scale'];

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');
    try {
      await upgradePlan(selected);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : u.errorTitle);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="h-full overflow-auto p-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-emerald-50 border border-emerald-100 p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-emerald-900 mb-2">{u.successTitle}</h1>
            <p className="text-emerald-800 mb-6">{u.successBody(t.plans[selected])}</p>
            {onBack && (
              <button
                onClick={onBack}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center justify-center gap-2 mx-auto transition-colors"
              >
                {u.backToProjects}
                <ArrowRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-display font-bold text-ink mb-3">{u.title}</h1>
          <p className="text-slate max-w-xl mx-auto">{u.subtitle}</p>
        </div>

        {user && (
          <div className="bg-white border border-cloud p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 flex items-center justify-center">
                <Zap size={20} className="text-red-500" />
              </div>
              <div>
                <p className="text-xs text-slate uppercase tracking-wider font-semibold">{u.currentPlan}</p>
                <p className="font-semibold text-ink">{t.plans[user.plan] || user.plan}</p>
              </div>
            </div>
            <span className="text-sm text-slate">
              {user.generations_used.toLocaleString()} / {user.generations_limit.toLocaleString()}
            </span>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 flex items-start gap-3 text-red-800">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {plans.map((plan) => {
            const data = t.landing.plans[plan];
            const active = selected === plan;
            return (
              <div
                key={plan}
                onClick={() => setSelected(plan)}
                className={`relative bg-white border p-6 cursor-pointer transition-all ${
                  active ? 'border-red-500 ring-1 ring-red-500' : 'border-cloud hover:border-red-300'
                }`}
              >
                {plan === 'founder' && (
                  <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                    {data.popular}
                  </div>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-ink">{data.name}</h3>
                    <p className="text-sm text-slate">{data.description}</p>
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                      active ? 'border-red-500 bg-red-500' : 'border-cloud'
                    }`}
                  >
                    {active && <Check size={14} className="text-white" />}
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-3xl font-extrabold text-black">{data.price}</span>
                  {'period' in data && (data as { period?: string }).period && (
                    <span className="text-sm text-ash">{(data as { period?: string }).period}</span>
                  )}
                </div>
                <ul className="space-y-2">
                  {data.features.map((feature: string) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-steel">
                      <Check size={14} className="shrink-0 mt-0.5 text-red-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="bg-red-50 border border-red-100 p-4 mb-6">
          <div className="flex items-start gap-3">
            <Crown size={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{u.billingNote}</p>
          </div>
        </div>

        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full py-3.5 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          {loading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <>
              {u.cta.replace('{plan}', t.plans[selected])}
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
